import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Copy, FileDown, Plus, Loader2, MapPin, ImageIcon, UserCog, History, Images, Link2, Power
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import {
  subscribeJob, subscribeStops, subscribeAudit, addStop, updateJob, assignDriver, setCustomerLinkEnabled, writePublicTracking, STOP_STATUS_META
} from "../lib/deliveries";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return "—"; }
}

export default function AdminJobDetail() {
  const { jobId } = useParams();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [stops, setStops] = useState([]);
  const [audit, setAudit] = useState([]);
  const [driverEmail, setDriverEmail] = useState("");
  const [notifyEmails, setNotifyEmails] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [ns, setNs] = useState({ recipient_name: "", company_name: "", address_line_1: "", city: "", state: "NY", zip_code: "", package_reference: "", delivery_instructions: "" });

  useEffect(() => {
    const u1 = subscribeJob(jobId, (j) => { setJob(j); setDriverEmail(j?.driver_email || ""); setNotifyEmails((j?.notify_emails || []).join(", ")); });
    const u2 = subscribeStops(jobId, setStops);
    const u3 = subscribeAudit(jobId, setAudit);
    return () => { u1(); u2(); u3(); };
  }, [jobId]);

  // Refresh the public mirror once so the token-keyed customer doc exists.
  useEffect(() => { writePublicTracking(jobId).catch(() => {}); }, [jobId]);

  if (!job) return <div className="container-518 py-20 text-center text-slate-500">Loading job…</div>;

  const counts = stops.reduce((a, s) => { a[s.status] = (a[s.status] || 0) + 1; return a; }, {});
  const trackUrl = `${window.location.origin}/track/${job.job_reference}/${job.track_token}`;

  const copy = () => { navigator.clipboard?.writeText(trackUrl); toast({ title: "Tracking link copied" }); };
  const customerUrl = `${window.location.origin}/delivery/${job.track_token}`;
  const linkEnabled = job.customer_link_enabled !== false;
  const copyCustomer = () => { navigator.clipboard?.writeText(customerUrl); toast({ title: "Customer link copied" }); };
  const toggleLink = async () => { await setCustomerLinkEnabled(job, !linkEnabled); toast({ title: linkEnabled ? "Customer link disabled" : "Customer link enabled" }); };
  const saveDriver = async () => { await assignDriver(jobId, driverEmail); toast({ title: "Driver assigned", description: driverEmail || "cleared" }); };
  const saveNotify = async () => {
    const list = notifyEmails.split(",").map((e) => e.trim()).filter(Boolean);
    await updateJob(jobId, { notify_emails: list });
    toast({ title: "Notification emails saved", description: `${list.length} recipient(s)` });
  };
  const submitStop = async (e) => {
    e.preventDefault();
    if (!ns.recipient_name || !ns.address_line_1) return;
    await addStop(jobId, ns, stops.length, job.driver_email);
    setNs({ recipient_name: "", company_name: "", address_line_1: "", city: "", state: "NY", zip_code: "", package_reference: "", delivery_instructions: "" });
    setShowAdd(false);
    toast({ title: "Stop added" });
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <section className="bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 py-8">
          <Link to="/admin/deliveries" className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white mb-3"><ArrowLeft className="w-4 h-4" /> All jobs</Link>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#f59321] mb-1">{job.job_reference} · {job.job_status}</div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold">{job.job_name}</h1>
          <p className="text-slate-300 mt-1 text-sm">{job.client_name} · {job.job_date} · Pickup: {job.pickup_address}</p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] max-w-md">
              <div className="flex justify-between text-xs mb-1"><span>Progress</span><span className="font-bold">{job.completed_stops || 0} / {job.total_stops || 0} delivered</span></div>
              <div className="h-2.5 bg-white/15 rounded-full overflow-hidden"><div className="h-full bg-[#f59321] rounded-full" style={{ width: `${job.total_stops ? (job.completed_stops / job.total_stops) * 100 : 0}%` }} /></div>
            </div>
            <Button onClick={copy} data-testid="copy-track-link" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-10 px-4 font-semibold"><Copy className="w-4 h-4 mr-1.5" /> Tracking link</Button>
            <Link to={`/admin/deliveries/${jobId}/pod`}><Button data-testid="open-manage-pod" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-10 px-4 font-semibold"><Images className="w-4 h-4 mr-1.5" /> Manage POD</Button></Link>
            <Link to={`/admin/deliveries/${jobId}/report`}><Button data-testid="open-report" className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-10 px-4 font-semibold"><FileDown className="w-4 h-4 mr-1.5" /> Report</Button></Link>
          </div>
        </div>
      </section>

      <section className="container-518 py-8 grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          {/* counts */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="progress-counts">
            {Object.entries(STOP_STATUS_META).map(([k, m]) => (
              <div key={k} className="bg-white rounded-xl border border-slate-200 p-3 text-center">
                <div className="font-display text-2xl font-extrabold text-[#0f2847]">{counts[k] || 0}</div>
                <div className="text-[11px] font-bold text-slate-500">{m.label}</div>
              </div>
            ))}
          </div>

          {/* assign driver */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><UserCog className="w-3.5 h-3.5" /> Assigned driver (email)</label>
              <Input value={driverEmail} onChange={(e) => setDriverEmail(e.target.value)} placeholder="driver@518courier.com" className="h-10 mt-1 rounded-lg border-slate-200" data-testid="assign-driver-input" />
            </div>
            <Button onClick={saveDriver} data-testid="assign-driver-save" className="bg-[#0f2847] text-white rounded-full h-10 px-5 font-semibold">Assign</Button>
          </div>

          {/* notification recipients */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-semibold text-slate-600">Delivery email notifications (comma-separated)</label>
              <Input value={notifyEmails} onChange={(e) => setNotifyEmails(e.target.value)} placeholder="client@example.com, ops@example.com" className="h-10 mt-1 rounded-lg border-slate-200" data-testid="notify-emails-input" />
              <p className="text-[11px] text-slate-400 mt-1">An email is sent to these addresses each time a stop is delivered.</p>
            </div>
            <Button onClick={saveNotify} data-testid="notify-emails-save" className="bg-[#0f2847] text-white rounded-full h-10 px-5 font-semibold">Save</Button>
          </div>

          {/* customer delivery proof link */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid="customer-portal-card">
            <div className="flex items-center gap-2 mb-2">
              <Link2 className="w-4 h-4 text-[#f59321]" />
              <h3 className="font-display font-bold text-[#0f2847] text-sm">Customer Delivery Proof link</h3>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${linkEnabled ? "bg-emerald-50 text-emerald-600 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"}`}>{linkEnabled ? "ACTIVE" : "DISABLED"}</span>
            </div>
            <p className="text-[11px] text-slate-400 mb-3">Share this secure, account-free link with the client to view/download POD photos and leave a review.</p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="flex-1 min-w-[220px] text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-slate-600 truncate">{customerUrl}</code>
              <Button onClick={copyCustomer} size="sm" variant="outline" className="rounded-full h-9 px-4 font-semibold border-2" data-testid="copy-customer-link"><Copy className="w-3.5 h-3.5 mr-1.5" /> Copy</Button>
              <a href={customerUrl} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="rounded-full h-9 px-4 font-semibold border-2">Preview</Button></a>
              <Button onClick={toggleLink} size="sm" className={`rounded-full h-9 px-4 font-semibold text-white ${linkEnabled ? "bg-red-500 hover:bg-red-600" : "bg-emerald-600 hover:bg-emerald-700"}`} data-testid="toggle-customer-link"><Power className="w-3.5 h-3.5 mr-1.5" /> {linkEnabled ? "Disable" : "Enable"}</Button>
            </div>
          </div>

          {/* stops */}
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-[#0f2847]">Stops ({stops.length})</h3>
              <Button onClick={() => setShowAdd((v) => !v)} size="sm" variant="outline" className="rounded-full h-9 px-4 font-semibold border-2" data-testid="toggle-add-stop"><Plus className="w-4 h-4 mr-1" /> Add stop</Button>
            </div>
            {showAdd && (
              <form onSubmit={submitStop} className="p-5 grid sm:grid-cols-2 gap-3 border-b border-slate-200 bg-slate-50" data-testid="add-stop-form">
                {[["recipient_name", "Recipient *"], ["company_name", "Company"], ["address_line_1", "Address *"], ["city", "City"], ["state", "State"], ["zip_code", "ZIP"], ["package_reference", "Package ref"], ["delivery_instructions", "Instructions"]].map(([k, l]) => (
                  <Input key={k} value={ns[k]} onChange={(e) => setNs((n) => ({ ...n, [k]: e.target.value }))} placeholder={l} className="h-10 rounded-lg border-slate-200" />
                ))}
                <Button type="submit" className="bg-[#f59321] text-white rounded-full h-10 font-semibold sm:col-span-2">Save stop</Button>
              </form>
            )}
            <ul className="divide-y divide-slate-100">
              {stops.map((s) => {
                const m = STOP_STATUS_META[s.status] || STOP_STATUS_META.pending;
                return (
                  <li key={s.id} className="px-5 py-4 flex items-center gap-4" data-testid={`admin-stop-${s.stop_number}`}>
                    <div className="w-8 text-center font-display font-extrabold text-slate-400">{String(s.stop_number).padStart(2, "0")}</div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-[#0f2847] text-sm truncate">{s.recipient_name}{s.company_name ? ` · ${s.company_name}` : ""}</div>
                      <div className="text-xs text-slate-500 truncate">{s.address_line_1}, {s.city} · {s.package_reference}</div>
                      {s.delivered_time && <div className="text-[11px] text-emerald-600 font-semibold mt-0.5">{fmt(s.delivered_time)}{s.recipient_received_by ? ` · ${s.recipient_received_by}` : ""}</div>}
                    </div>
                    {s.photo_url && <a href={s.photo_url} target="_blank" rel="noreferrer" className="text-slate-400 hover:text-[#f59321]"><ImageIcon className="w-4 h-4" /></a>}
                    <span className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* audit */}
        <div className="bg-white rounded-2xl border border-slate-200 p-5 h-fit">
          <h3 className="font-display font-bold text-[#0f2847] flex items-center gap-2 mb-3"><History className="w-4 h-4" /> Audit trail</h3>
          <ul className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="audit-trail">
            {audit.length === 0 ? <li className="text-sm text-slate-400">No activity yet.</li> : audit.map((a) => (
              <li key={a.id} className="text-xs text-slate-600 border-l-2 border-slate-200 pl-3 py-0.5">
                <span className="font-semibold text-[#0f2847]">{a.message}</span>
                <div className="text-slate-400">{a.at?.toDate ? a.at.toDate().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""} · {a.actor}</div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
